import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, alpha } from '@mui/material/styles';
import { useSystemTheme } from '../hooks/useSystemTheme';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme deve ser usado dentro de ThemeProvider');
  }
  return context;
};

const createAppTheme = (isDark) => createTheme({
  palette: {
    mode: isDark ? 'dark' : 'light',
    primary: {
      main: '#5FBB9E',
      light: '#8BD0B8',
      dark: '#4A9B82',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#5BA3C7',
      light: '#7FB3D3',
      dark: '#4A8BB0',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#27AE60',
      light: '#58C77C',
      dark: '#1E8E4F',
    },
    warning: {
      main: '#F39C12',
      light: '#F5B041',
      dark: '#D68910',
    },
    error: {
      main: '#E74C3C',
      light: '#EC7063',
      dark: '#C0392B',
    },
    info: {
      main: '#3498DB',
      light: '#5DADE2',
      dark: '#2E86C1',
    },
    background: {
      default: isDark ? '#2C3E50' : '#FFFFFF',
      paper: isDark ? '#495057' : '#FAFDFB',
    },
    grey: {
      50: '#F8F9FA',
      100: '#E9ECEF',
      200: '#E3F5F0',
      300: '#8BD0B8',
      400: '#7A9BAD',
      500: '#6C757D',
      600: '#495057',
      700: '#357A65',
      800: '#2C3E50',
      900: '#2C5441',
    },
    text: {
      primary: isDark ? '#FFFFFF' : '#2C5441',
      secondary: isDark ? '#E9ECEF' : '#6C757D',
    },
    divider: alpha(isDark ? '#6C757D' : '#E3F5F0', 0.8),
  },

  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.25rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontSize: '1.875rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
      color: isDark ? '#94a3b8' : '#64748b',
    },
  },

  spacing: 8,

  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
      xxl: 1536,
    },
  },

  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: isDark ? '#495057' : '#5FBB9E',
          color: '#FFFFFF',
          boxShadow: '0 1px 3px 0 rgba(95, 187, 158, 0.12)',
          borderBottom: `1px solid ${isDark ? '#6C757D' : '#4A9B82'}`,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: isDark ? '#495057' : '#F8FCFA',
          borderRight: `1px solid ${isDark ? '#6C757D' : '#E3F5F0'}`,
          boxShadow: 'none',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgba(95, 187, 158, 0.12)',
          border: `1px solid ${isDark ? '#6C757D' : '#E3F5F0'}`,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          padding: '10px 16px',
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 2px 4px 0 rgb(0 0 0 / 0.1)',
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            transition: 'all 0.2s ease-in-out',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#5FBB9E',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: '2px',
              borderColor: '#5FBB9E',
            },
          },
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          margin: '2px 8px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha('#5FBB9E', 0.08),
            transform: 'translateX(4px)',
          },
          '&.Mui-selected': {
            backgroundColor: alpha('#5FBB9E', 0.12),
            color: '#5FBB9E',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: alpha('#5FBB9E', 0.16),
            },
            '& .MuiListItemIcon-root': {
              color: '#5FBB9E',
            },
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 500,
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px 0 rgb(0 0 0 / 0.1)',
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: alpha(isDark ? '#475569' : '#e2e8f0', 0.8),
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          paddingLeft: '16px !important',
          paddingRight: '16px !important',
          minHeight: '64px !important',
          '@media (min-width: 640px)': {
            paddingLeft: '24px !important',
            paddingRight: '24px !important',
          },
          '@media (min-width: 1024px)': {
            paddingLeft: '20px !important',
            paddingRight: '20px !important',
            minHeight: '60px !important',
          },
          '@media (min-width: 1536px)': {
            paddingLeft: '24px !important',
            paddingRight: '24px !important',
            minHeight: '56px !important',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: isDark ? '#94a3b8' : '#64748b',
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: isDark ? '#f8fafc' : '#0f172a',
        },
        secondary: {
          color: isDark ? '#94a3b8' : '#64748b',
        },
      },
    },

    MuiTypography: {
      styleOverrides: {
        root: {
          color: isDark ? '#f8fafc' : '#0f172a',
        },
      },
    },

  },

  transitions: {
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
});

export const ThemeProvider = ({ children }) => {
  const systemIsDark = useSystemTheme();
  const [themeMode, setThemeMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    return saved || 'system';
  });

  const isDark = themeMode === 'system' ? systemIsDark : themeMode === 'dark';
  const theme = createAppTheme(isDark);

  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
    // Adiciona atributo data-theme ao body para CSS customizado
    document.body.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [themeMode, isDark]);

  const toggleTheme = () => {
    setThemeMode(prev => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'system';
      return 'light';
    });
  };

  const value = {
    themeMode,
    isDark,
    toggleTheme,
    setThemeMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};